import { z } from 'zod';
import { FEATURE_AVAILABILITY_REASONS, FEATURE_CATEGORIES, FEATURE_KEYS, FEATURE_LOCALES, } from '../features/index.js';
import { isoDateTimeSchema } from './common.js';
export const featureKeySchema = z.enum(FEATURE_KEYS);
export const featureCategorySchema = z.enum(FEATURE_CATEGORIES);
export const featureAvailabilityReasonSchema = z.enum(FEATURE_AVAILABILITY_REASONS);
export const localizedFeatureTextSchema = z.object(Object.fromEntries(FEATURE_LOCALES.map((locale) => [locale, z.string().min(1)])));
export const featureDefinitionSchema = z.object({
    key: featureKeySchema,
    name: localizedFeatureTextSchema,
    description: localizedFeatureTextSchema,
    category: featureCategorySchema,
    icon: z.string().min(1),
    defaultEnabled: z.boolean(),
    dependencies: z.array(featureKeySchema),
    dependents: z.array(featureKeySchema),
    requiredPermissions: z.array(z.string().min(1)),
    backendModules: z.array(z.string().min(1)),
    frontendRoutes: z.array(z.string().min(1)),
    configurationRoutes: z.array(z.string().min(1)),
    navigationEntries: z.array(z.string().min(1)),
    workers: z.array(z.string().min(1)),
    schedulers: z.array(z.string().min(1)),
    publicEndpoints: z.array(z.string().min(1)),
    webhooks: z.array(z.string().min(1)),
    requiredConfiguration: z.array(z.string().min(1)),
    resourceHint: localizedFeatureTextSchema,
    retentionBehavior: localizedFeatureTextSchema,
    disableStrategy: localizedFeatureTextSchema,
    reactivateStrategy: localizedFeatureTextSchema,
    shutdownMode: z.enum([
        'always-on',
        'deny-new-work-and-finish-running',
        'deny-and-pause-ingress',
        'read-only-history',
    ]),
    systemCore: z.boolean(),
    platform: z.object({
        available: z.boolean(),
        eligiblePlans: z.array(z.string().min(1)),
        meteredResource: z.string().nullable(),
    }),
});
export const tenantFeatureStateSchema = z.object({
    key: featureKeySchema,
    desiredEnabled: z.boolean(),
    effectiveEnabled: z.boolean(),
    effectiveReason: featureAvailabilityReasonSchema,
    reason: z.string().nullable(),
    blockingDependencies: z.array(featureKeySchema),
    missingConfiguration: z.array(z.string().min(1)),
    optimisticVersion: z.number().int().positive(),
    changedById: z.string().nullable(),
    changedAt: isoDateTimeSchema,
});
export const effectiveFeatureCatalogueEntrySchema = featureDefinitionSchema.merge(tenantFeatureStateSchema.omit({ key: true }));
export const effectiveFeatureCatalogueSchema = z.object({
    features: z.array(effectiveFeatureCatalogueEntrySchema),
    generatedAt: isoDateTimeSchema,
});
export const portalFeatureAvailabilitySchema = z.object({
    customerPortal: z.boolean(),
    documents: z.boolean(),
    orders: z.boolean(),
    generatedAt: isoDateTimeSchema,
});
export const featureMigrationIssueSchema = z.object({
    legacyKey: z.string().min(1),
    reason: z.string().min(1),
    detectedAt: isoDateTimeSchema,
});
export const tenantFeaturePreferenceUpdateSchema = z
    .object({
    desiredEnabled: z.boolean(),
    expectedVersion: z.number().int().positive(),
    reason: z.string().trim().min(3).max(1_000).nullable().default(null),
})
    .strict();
export const featureImpactCountSchema = z.object({
    key: z.string().min(1),
    count: z.number().int().nonnegative(),
});
export const featureImpactAnalysisSchema = z.object({
    featureKey: featureKeySchema,
    requestedEnabled: z.boolean(),
    currentDesiredEnabled: z.boolean(),
    currentEffectiveEnabled: z.boolean(),
    affectedFeatures: z.array(featureKeySchema),
    dependentFeatures: z.array(featureKeySchema),
    activeJobs: z.array(featureImpactCountSchema),
    scheduledCampaigns: z.number().int().nonnegative(),
    openBusinessRecords: z.array(featureImpactCountSchema),
    publicEndpoints: z.array(z.string()),
    webhooks: z.array(z.string()),
    affectedPortalUsers: z.number().int().nonnegative(),
    dataRecordCount: z.number().int().nonnegative(),
    consequences: localizedFeatureTextSchema,
    reactivation: localizedFeatureTextSchema,
    blockingReasons: z.array(z.string().min(1)),
    missingConfiguration: z.array(z.string().min(1)).default([]),
    reasonRequired: z.boolean(),
    requiresConfirmation: z.boolean(),
    expectedVersions: z.record(featureKeySchema, z.number().int().positive()),
});
export const featureChangePlanApplySchema = z
    .object({
    requestedEnabled: z.boolean(),
    affectedFeatures: z.array(featureKeySchema).min(1),
    expectedVersions: z.record(featureKeySchema, z.number().int().positive()),
    reason: z.string().trim().min(3).max(1_000).nullable(),
    confirmImpact: z.literal(true),
})
    .strict();
export const featureChangePreviewRequestSchema = z
    .object({ requestedEnabled: z.boolean() })
    .strict();
export const featureKeyParamsSchema = z.object({ featureKey: featureKeySchema }).strict();
export const featureOperationalSummarySchema = z.object({
    featureKey: featureKeySchema,
    dataRecordCount: z.number().int().nonnegative(),
    activeJobs: z.array(featureImpactCountSchema),
    pausedJobs: z.number().int().nonnegative(),
    openBusinessRecords: z.array(featureImpactCountSchema),
});
export const featureOperationalSummaryListSchema = z.object({
    data: z.array(featureOperationalSummarySchema),
    generatedAt: isoDateTimeSchema,
});
export const featureHistoryQuerySchema = z
    .object({
    page: z.coerce.number().int().positive().default(1),
    pageSize: z.coerce.number().int().min(10).max(100).default(25),
})
    .strict();
export const featureHistoryEntrySchema = z.object({
    id: z.string().min(1),
    featureKey: featureKeySchema,
    rootFeatureKey: featureKeySchema,
    desiredEnabledBefore: z.boolean(),
    desiredEnabledAfter: z.boolean(),
    reason: z.string().nullable(),
    actorName: z.string().nullable(),
    occurredAt: isoDateTimeSchema,
});
export const featureHistoryResponseSchema = z.object({
    data: z.array(featureHistoryEntrySchema),
    page: z.number().int().positive(),
    pageSize: z.number().int().positive(),
    total: z.number().int().nonnegative(),
});
//# sourceMappingURL=features.js.map